/**
 * WorkOrderAppLoggingService.js
 *
 * Centralized logging utility for work order app events. Use logWorkOrderAppEvent to send structured logs
 * to the backend for auditing, debugging, and analytics. All log fields are serialized, truncated, and
 * circular references are handled automatically. Always use this service for app-level logging instead of
 * direct API calls or console logs.
 *
 * Usage:
 *   import WorkOrderAppLoggingService from './WorkOrderAppLoggingService';
 *   await WorkOrderAppLoggingService.logWorkOrderAppEvent({ ...fields });
 *
 * See function-level comments for details on parameters and serialization.
 */
import { api } from '../globalConstants'
import { postData } from './HttpService'

const MAX_LOG_FIELD_LENGTH = 5000
const TRUNCATED_SUFFIX = '...[truncated]'

// Trims a string value to a maximum length for log fields, appending a suffix if truncated.
const trimLogValue = (value) => {
  if (!value) {
    return ''
  }

  return value.length > MAX_LOG_FIELD_LENGTH
    ? `${value.slice(
        0,
        MAX_LOG_FIELD_LENGTH - TRUNCATED_SUFFIX.length,
      )}${TRUNCATED_SUFFIX}`
    : value
}

// Converts an Error object into a plain object for safe serialization in logs.
const serializeError = (error) => {
  if (!error) {
    return error
  }

  return {
    name: error?.name,
    message: error?.message,
    stack: error?.stack,
    code: error?.code,
    status: error?.response?.status,
    statusText: error?.response?.statusText,
    responseData: error?.response?.data,
    requestUrl: error?.config?.url,
    requestMethod: error?.config?.method,
  }
}

/**
 * Returns a replacer function for JSON.stringify that:
 *   - Converts Error objects to plain objects (using serializeError)
 *   - Replaces circular references with the string '[Circular]'
 *
 * This ensures that complex objects (including those with cycles or errors) can be safely serialized for logging.
 * Usage: JSON.stringify(obj, getCircularReplacer())
 */
const getCircularReplacer = () => {
  const seen = new WeakSet()

  return (_, value) => {
    if (value instanceof Error) {
      return serializeError(value)
    }

    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular]'
      }

      seen.add(value)
    }

    return value
  }
}

// Serializes any value for logging: handles strings, objects, errors, and circular references. Truncates long output.
const serializeLogField = (value) => {
  if (value === undefined || value === null || value === '') {
    return ''
  }

  if (typeof value === 'string') {
    return trimLogValue(value)
  }

  try {
    return trimLogValue(JSON.stringify(value, getCircularReplacer()))
  } catch (error) {
    return trimLogValue(
      JSON.stringify({
        serializationError: serializeError(error),
      }),
    )
  }
}

/**
 * Sends a structured work order app event log to the backend.
 * All fields are serialized and truncated for safe logging.
 *
 * @param {Object} fields - Log fields (all optional except userId)
 * @param {string} fields.userId - User identifier (required for backend association)
 * @param {string} fields.method - Name of the method or action being logged
 * @param {any} fields.params - Parameters or input data for the action
 * @param {string} fields.tsFile - Source file or module name
 * @param {string} fields.maximoUrl - Related Maximo API URL (if applicable)
 * @param {string} fields.httpMethod - HTTP method used (if applicable)
 * @param {any} fields.body - Request body (if applicable)
 * @param {any} fields.response - Response data (if applicable)
 * @param {any} fields.miscLogs - Any additional log info
 *
 * @returns {Promise<Object|null>} The payload sent, or null if logging failed
 */
export const logWorkOrderAppEvent = async ({
  userId,
  method,
  params,
  tsFile,
  maximoUrl,
  httpMethod,
  body,
  response,
  miscLogs,
}) => {
  const payload = {
    method: serializeLogField(method),
    params: serializeLogField(params),
    tsfile: serializeLogField(tsFile),
    maximourl: serializeLogField(maximoUrl),
    httpmethod: serializeLogField(httpMethod),
    body: serializeLogField(body),
    response: serializeLogField(response),
    misclogs: serializeLogField(miscLogs),
  }

  try {
    await postData(
      api.workOrderAppLogs(userId || 'user'),
      JSON.stringify(payload),
    )
  } catch (error) {
    // Swallow errors to avoid breaking app flow if logging fails
    return null
  }

  return payload
}

export default {
  logWorkOrderAppEvent,
}
